<!--
	@component

	Heading allows you to create a h1-h6 element (set by `level` prop).

	## Setting content
	The component doesn't provide any styling, but offers `text` prop, which will be passed

	a) `Formatted` component, if you use it empty

	```svelte
		<Heading level={2} text="We are the company" />
	```

	b) `AriaText` component, if you use it with a content

	This can be helpful for embedding medias (like logos) as a heading,
	if there isn't any other solution.

	```svelte
		<Heading level={1} text="Company Inc.">
			<Logo />
		</Heading>
	```

	In that case you might use an empty string to escape that behaviour.

	## Complex

	If you need a reference to the DOM element, you might use `bind:element` (**not** `bind:this`).
	But remember – a heading shouldn't be a complex thing :)
-->
<script lang="ts" module>
	import type { HTMLAttributes } from "svelte/elements";
	import AriaText from "./AriaText.svelte";
	import Formatted from "./Formatted.svelte";
	import type { HeadingLevel } from "./HeadingLevel.js";

	export interface HeadingProps extends Omit<HTMLAttributes<HTMLHeadingElement>, `${"bind" | "on"}:${string}`> {
		readonly element?: HTMLHeadingElement;
		level: HeadingLevel;
		text: string;
	}
</script>

<script lang="ts">
	let {
		/* eslint-disable prefer-const */
		text,
		level,
		children,
		/* eslint-enable prefer-const */
		element = $bindable(),
		// eslint-disable-next-line prefer-const
		...props
	}: HeadingProps = $props();
	const tag = $derived(`h${level}` satisfies keyof HTMLElementTagNameMap);
	const componentId = $props.id();
	const ariaLabelledBy = $derived(children ? undefined : componentId);
</script>

<svelte:element this={tag} aria-labelledby={ariaLabelledBy} {...props} bind:this={element}>
	{#if children}
		<AriaText aria={text} id={ariaLabelledBy}>
			{@render children()}
		</AriaText>
	{:else}
		<Formatted {text} />
	{/if}
</svelte:element>
